import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, LogIn, Download } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { APP } from "@/constants/testIds";

const HERO_BG =
  "https://images.pexels.com/photos/9118032/pexels-photo-9118032.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Google OAuth è gestito da Emergent e funziona solo su domini *.preview.emergentagent.com.
// Su installazioni self-hosted (VPS) il pulsante viene nascosto impostando la variabile
// REACT_APP_GOOGLE_AUTH_ENABLED=false in frontend/.env
const GOOGLE_AUTH_ENABLED =
  (process.env.REACT_APP_GOOGLE_AUTH_ENABLED ?? "true").toLowerCase() !== "false";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestModal, setRequestModal] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-password", { email: email.trim(), password });
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      setUser(data.user);
      toast.success(`Bentornato, ${data.user.name || data.user.email}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left – Hero */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.65), rgba(30,64,175,0.85)), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-700 flex items-center justify-center rounded-md shadow-lg">
            <span className="font-display font-black text-white text-2xl leading-none">E</span>
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none tracking-wide">ETHOS</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-orange-400 font-semibold">
              Costruzioni
            </div>
          </div>
        </div>
        <div>
          <div className="overline text-orange-400 mb-4">Piattaforma integrata</div>
          <h1 className="font-display text-4xl xl:text-6xl font-black leading-tight mb-6">
            Ogni cantiere.<br /> Ogni euro.<br /> Sotto controllo.
          </h1>
          <p className="text-slate-200 max-w-md text-lg leading-relaxed">
            Presenze GPS, rapportini, bolle, cronoprogramma, cash flow e BEP.
            L'app installabile su iPhone e Android per gestire la tua impresa in mobilità.
          </p>
        </div>
        <div className="text-sm text-slate-300">
          Accedi con Google (rapido) oppure email/password (qualsiasi provider: Aruba, Libero, PEC, ecc.).
        </div>
      </div>

      {/* Right – Login */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-11 h-11 bg-blue-700 flex items-center justify-center rounded-md">
              <span className="font-display font-black text-white text-xl leading-none">E</span>
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-wide">ETHOS</div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-orange-500 font-semibold">
                Costruzioni
              </div>
            </div>
          </div>

          <div className="overline mb-3 text-blue-700">Accedi al tuo account</div>
          <h2 className="font-display text-3xl lg:text-4xl font-black mb-3">
            Benvenuto in Ethos.
          </h2>
          <p className="text-slate-600 mb-6">
            {GOOGLE_AUTH_ENABLED
              ? "Accedi con Google o con la tua email aziendale."
              : "Accedi con la tua email aziendale."}
          </p>

          {GOOGLE_AUTH_ENABLED && (
            <>
              {/* Google */}
              <button
                onClick={handleGoogle}
                data-testid={APP.googleLoginButton}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white border-2 border-slate-300 text-slate-800 font-semibold hover:bg-slate-50 transition-colors rounded-sm"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 7 29.3 5 24 5c-7.6 0-14.2 4.3-17.7 10.7z"/>
                  <path fill="#4CAF50" d="M24 43c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.6 38.5 16.3 43 24 43z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.4 34.9 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Continua con Google
              </button>

              <div className="flex items-center gap-3 my-5">
                <div className="h-px bg-slate-200 flex-1" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">oppure</span>
                <div className="h-px bg-slate-200 flex-1" />
              </div>
            </>
          )}

          {/* Email / Password */}
          <form onSubmit={handlePasswordLogin} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  placeholder="nome@aruba.it, libero.it, pec.it, gmail.com..."
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="La tua password"
                  className="w-full pl-9 pr-10 py-2.5 border border-slate-300 rounded-sm text-sm"
                  autoComplete="current-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  data-no-uppercase
                  style={{ textTransform: "none" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label={showPw ? "Nascondi password" : "Mostra password"}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="btn-login-password"
              className="w-full flex items-center justify-center gap-2 py-3 bg-blue-700 text-white font-semibold hover:bg-blue-800 rounded-sm shadow-brutal disabled:opacity-60"
            >
              <LogIn className="w-4 h-4" />
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>

          <div className="mt-3 flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-blue-700 hover:underline font-semibold" data-testid="link-forgot">
              Password dimenticata?
            </Link>
            <button
              type="button"
              onClick={() => setRequestModal(true)}
              data-testid="btn-request-invite"
              className="text-blue-700 hover:underline font-semibold"
            >
              Non hai un account? Chiedi l'invito
            </button>
          </div>

          {/* Install PWA hint */}
          <div className="mt-6 flex items-start gap-3 p-4 border border-blue-200 bg-blue-50 rounded-md">
            <Download className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
            <div className="text-xs text-blue-900 leading-relaxed">
              <b>Installa Ethos sul tuo smartphone.</b> Chrome/Android: prompt automatico.
              iPhone/Safari: <span className="whitespace-nowrap">Condividi → "Aggiungi alla schermata Home".</span>
            </div>
          </div>
        </div>
      </div>

      {requestModal && <RequestInviteModal onClose={() => setRequestModal(false)} />}
    </div>
  );
}

function RequestInviteModal({ onClose }) {
  const [form, setForm] = useState({ email: "", name: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      await api.post("/auth/request-invite", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="font-display text-lg font-bold">Richiedi un invito</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" aria-label="Chiudi">
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
        {!sent ? (
          <form onSubmit={submit}>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-600">
                Compila i dati sotto: l'amministratore riceverà la tua richiesta
                nella sezione Utenti &amp; Ruoli e ti invierà un link di invito.
              </p>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">La tua email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  autoFocus
                  data-testid="req-invite-email"
                  placeholder="nome@aruba.it"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Nome e cognome</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="req-invite-name"
                  placeholder="Mario Rossi"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Messaggio (facoltativo)</label>
                <textarea
                  rows={2}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="req-invite-message"
                  placeholder="Sono un carpentiere di Ethos Costruzioni…"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white">
                Annulla
              </button>
              <button type="submit" disabled={loading} data-testid="btn-submit-request-invite" className="px-4 py-2 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-sm disabled:opacity-60">
                {loading ? "Invio…" : "Invia richiesta"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 text-center">
            <div className="text-4xl mb-2">✅</div>
            <h3 className="font-display text-lg font-black mb-1">Richiesta inviata</h3>
            <p className="text-sm text-slate-600">
              L'amministratore riceverà la tua richiesta e ti invierà un link di invito appena possibile.
            </p>
            <button onClick={onClose} className="mt-5 py-2 px-4 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-slate-50">
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
